import React, { useEffect, useMemo, useState } from 'react';
import './ChangeRequestsScreen.less';
import Meta from '../../components/Meta';
import { Result } from '../../interfaces/changeRequest';
import { useNavigate } from 'react-router-dom';
import { Notify } from '../../services/notificationService';
import { NOT_FOUND, NOT_OWN_CHANGE_REQUEST } from '../../constants/texts';
import { confirmAction } from '../../utils/confirmActionModal';
import { useDispatch, useSelector } from 'react-redux';
import { useAgentSelector, useAgentDispatch } from '../../hooks/useAgentStore';
import { AppDispatch, RootState } from '../../app/store';
import { isUserReadonly } from '../../services/subscription';
import { deleteChangeRequests } from '../../app/ActionTypes/changeRequestsActions';
import ChangeRequestsTable from '../../components/ChangeRequest/ChangeRequestsTable/ChangeRequestsTable';
import ChangeRequestsTableCTAs from '../../components/ChangeRequest/ChangeRequestsTableCTAs/ChangeRequestsTableCTAs';
import { ChangeRequestListingScope } from '../../enums/changeRequest';
import { isOwnChangeRequest } from '../../services/changeRequestService';
import ChangeRequestScopeSelect from '../../components/AppSideMenu/ChangeRequestScopeSelect';
import { setChangeRequestsListingScope } from '../../app/Slices/ChangeRequestItemsSlice';
import { useUserAccess } from '../../hooks/useUserAccess';
import { selectSubscriptionStatus } from '../../app/Slices/subscriptionSlice';

const ChangeRequestsScreen: React.FC = () => {
  const navigate = useNavigate();
  const { isTeamUser } = useUserAccess();

  const [selectedRows, setSelectedRows] = useState<Result[]>([]);
  const dispatch = useDispatch<AppDispatch>();
  const agentDispatch = useAgentDispatch();

  const status = useSelector(selectSubscriptionStatus);

  const myChangeRequestsState = useAgentSelector(
    (state) => state.changeRequestItems.myChangeRequests
  );
  const allChangeRequestsState = useAgentSelector(
    (state) => state.changeRequestItems.allChangeRequests
  );
  const org = useSelector((state: RootState) => state.organization.org);
  const isReadonly = isUserReadonly(status);

  const listingScope = useAgentSelector(
    (state) => state.changeRequestItems.listingScope
  );

  // Force reset state of the CR table when the listing scope changes
  const [tableKey, setTableKey] = useState(0);
  useEffect(() => {
    setTableKey((prev) => prev + 1);
  }, [listingScope]);

  const tableProps = useMemo(() => {
    if (listingScope === ChangeRequestListingScope.MY) {
      return {
        changeRequests: myChangeRequestsState.records,
        loading: myChangeRequestsState.loading,
        withAuthor: false,
      };
    }
    return {
      changeRequests: allChangeRequestsState.records,
      loading: allChangeRequestsState.loading,
      withAuthor: true,
    };
  }, [listingScope, myChangeRequestsState, allChangeRequestsState]);

  const handleDeleteChangeRequest = async (records: Result | Result[]) => {
    const recordsArray = Array.isArray(records) ? records : [records];
    const ids = recordsArray.map((record) => record.id).filter(Boolean);

    if (ids.length === 0) {
      Notify.error(NOT_FOUND);
      return;
    }

    const isConfirmed = await confirmAction({
      title: `Are you sure you want to delete ${ids.length > 1 ? 'these Change Requests?' : 'this Change Request?'}`,
      okText: 'Delete',
      okType: 'danger',
    });
    if (!isConfirmed) return;

    try {
      await dispatch(deleteChangeRequests({ ids }));
      setSelectedRows([]);
    } catch (error) {
      Notify.error((error as Error).message || 'Unable to perform action');
    }
  };

  const handleChangeListingScope = (newValue: ChangeRequestListingScope) => {
    agentDispatch(setChangeRequestsListingScope(newValue));
  };

  return (
    <>
      <Meta name="Cirra AI - Change Requests" />
      <div
        id="change-requests"
        className="main-screen-element"
        data-testid="change-requests"
      >
        <div className="header-bar">
          <div className="title">Change Requests</div>
          <div className="header-bar-actions">
            <ChangeRequestsTableCTAs
              selectedRows={selectedRows}
              loading={allChangeRequestsState.loading}
              isReadonly={isReadonly}
              onCreate={() => navigate('/change-requests/new')}
              onBatchDelete={handleDeleteChangeRequest}
            />
          </div>
        </div>

        {isTeamUser && (
          <div className="listing-scope-select-container">
            View:&nbsp;
            <ChangeRequestScopeSelect
              className="listing-scope-select"
              size="middle"
              scope={listingScope}
              onChange={handleChangeListingScope}
            />
          </div>
        )}

        <ChangeRequestsTable
          key={tableKey}
          className="change-requests-table"
          {...tableProps}
          disableSelection={(cr) => {
            const isOwnCr = isOwnChangeRequest(org, cr);
            return isOwnCr ? null : NOT_OWN_CHANGE_REQUEST;
          }}
          setSelectedRows={setSelectedRows}
        />
      </div>
    </>
  );
};

export default ChangeRequestsScreen;
